import { ChevronLeft } from "lucide-react";
import {
  SimpleKitModal,
  SimpleKitModalBody,
  SimpleKitModalContent,
  SimpleKitModalDescription,
  SimpleKitModalFooter,
  SimpleKitModalHeader,
  SimpleKitModalTitle,
} from "./web3modal";
import { useAccount, useDisconnectAccount } from "./hooks";
import { OptionButton } from "./components/OptionButton";
import { LogoutLogo } from "based-assets";
import {
  titles,
  routes,
  useSimpleKitContext,
} from "./context/SimpleKitContext";
import { SelectETHWallet } from "./sections/SelectETHWallet";
import { SelectSolanaWallet } from "./sections/SelectSolanaWallet";
import { WalletQR } from "./sections/WalletQR";
import { WalletConnecting } from "./sections/WalletConnecting";
import { SwitchingChain } from "./sections/SwitchingChain";
import { SwitchAccount } from "./sections/SwitchAccount";
import { Account } from "./sections/Account";
import { Home } from "./sections/Home";
import { NetworkSymbols } from "based-auth/networks";
import { SwitchChain } from "./sections/SwitchChain";
import { SwitchingAccount } from "./sections/SwitchingAccount";
import { WithdrawFunds } from "./sections/Withdraw";
import { useEffect } from "react";
import { SocialConnecting } from "./sections/SocialConnecting";
import { AccountSigning } from "./sections/AccountSigning";
import { SimpleKit } from "./context/SimpleKit";

const pages = {
  [routes.home]: <Home />,
  [routes.account]: <Account />,
  [routes.accountSigning]: <AccountSigning />,
  [routes.switchChain]: <SwitchChain />,
  [routes.switchingChain]: <SwitchingChain />,
  [routes.switchAccount]: <SwitchAccount />,
  [routes.switchingAccount]: <SwitchingAccount />,
  [routes.selectETHWallet]: <SelectETHWallet />,
  [routes.selectSolanaWallet]: <SelectSolanaWallet />,
  [routes.walletConnecting]: <WalletConnecting />,
  [routes.socialConnecting]: <SocialConnecting />,
  [routes.walletqr]: <WalletQR />,
  [routes.withdrawFunds]: <WithdrawFunds />,
};

export function SimpleKitModalProvider(props: any) {
  const { address, chainId } = useAccount();

  const { router, setRouter, open, setOpen, pendingItem, setPendingItem } =
    useSimpleKitContext();

  useEffect(() => {
    setOpen(false);
    setPendingItem(null);
    setRouter(address ? routes.account : routes.home);
  }, [address, chainId]);

  useEffect(() => {
    if (!open) {
      setRouter(address ? routes.account : routes.home);
    }
  }, [open]);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "style"
        ) {
          const pointerEvents = document.body.style.pointerEvents;
          if (pointerEvents === "none" && !open) {
            console.log("pointerEvents removed");
            document.body.style.pointerEvents = "";
          }
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      {props.children}
      <SimpleKit />
      <SimpleKitModal open={open} onOpenChange={setOpen}>
        <SimpleKitModalContent>
          <SimpleKitModalHeader>
            <BackChevron />
            <SimpleKitModalTitle>
              {router && router in titles
                ? titles[router as keyof typeof titles]
                : "Login"}
            </SimpleKitModalTitle>
            <SimpleKitModalDescription className="web3kit-sr-only">
              Connect your Web3 wallet or create a new one.
            </SimpleKitModalDescription>
          </SimpleKitModalHeader>
          <SimpleKitModalBody>
            {pages[router] || <>{!address ? <Home /> : <Account />}</>}
          </SimpleKitModalBody>
          <SimpleKitModalFooter>
            <SimpleKitModalFooterContent />
          </SimpleKitModalFooter>
        </SimpleKitModalContent>
      </SimpleKitModal>
    </>
  );
}

function SimpleKitModalFooterContent() {
  const { address } = useAccount();
  const { pendingItem } = useSimpleKitContext();

  return <>{address && !pendingItem && <DisAccountButton />}</>;
}

function DisAccountButton() {
  const { mutateAsync: disconnectAccount, isPending } = useDisconnectAccount();

  return (
    <OptionButton
      logo={LogoutLogo}
      disabled={isPending}
      onClick={() => disconnectAccount()}
    >
      {isPending ? "Signing out" : "Logout"}
    </OptionButton>
  );
}

function BackChevron() {
  const { address } = useAccount();
  const { router, setRouter, pendingItem, setPendingItem } =
    useSimpleKitContext();

  if (!router) {
    return null;
  }

  if (router === routes.home && !address) {
    return null;
  }

  if (router === routes.account && address) {
    return null;
  }

  function handleClick() {
    if (pendingItem) {
      setPendingItem(() => {
        setRouter(!address ? routes.home : routes.account);
        return null;
      });
      return;
    }

    switch (router) {
      case routes.home:
      case routes.switchChain:
      case routes.switchAccount:
      case routes.walletqr:
      case routes.withdrawFunds:
        setRouter(routes.account);
        break;
      default:
        setRouter(routes.home);
    }
  }

  return (
    <button
      className="web3kit-absolute web3kit-left-[26px] web3kit-top-[42px] web3kit-z-20 web3kit-rounded-sm web3kit-opacity-70 web3kit-ring-offset-background web3kit-transition-opacity hover:web3kit-opacity-100 focus:web3kit-outline-none focus:web3kit-ring-2 focus:web3kit-ring-ring focus:web3kit-ring-offset-2 disabled:web3kit-pointer-events-none data-[state=open]:web3kit-bg-accent data-[state=open]:web3kit-text-muted-foreground md:web3kit-top-[26px]"
      onClick={handleClick}
    >
      <ChevronLeft className="web3kit-h-4 web3kit-w-4" />
      <span className="web3kit-sr-only">Cancel connection</span>
    </button>
  );
}
